import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors';

// API 호출을 위한 타입 정의
interface MentorAnalysis {
  mentorId: string;
  mentorNameKr: string;
  mentorNameEn: string;
  analysisResult: {
    ko: {
      overallEvaluation: string;
      learningPoints: string;
      nextCheckpoint: string;
      riskAssessment: string;
      investmentPhilosophy: string;
    };
    en: {
      overallEvaluation: string;
      learningPoints: string;
      nextCheckpoint: string;
      riskAssessment: string;
      investmentPhilosophy: string;
    };
  };
}

interface DailyMentorAnalysisResponse {
  id: string;
  stockCode: string;
  stockNameKr: string;
  stockNameEn: string;
  analysisPerMentor: MentorAnalysis[];
  createdAt: string;
  updatedAt: string;
}

interface SummaryItem {
  title: string;
  content: string;
}

const DemoContainer = styled.section`
  padding: 80px 0;
  background: linear-gradient(180deg, ${colors.backgroundSecondary} 0%, white 100%);
  overflow: hidden;
`;

const DemoContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 40px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 32px;
  }
  
  .desktop-text {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-text {
    @media (min-width: 769px) {
      display: none;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: ${colors.textSecondary};
  margin-bottom: 48px;
  line-height: 1.6;
`;

// 데스크탑용 가로 스크롤 컨테이너
const DesktopCarousel = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopCardsContainer = styled.div<{ current: number; cardWidth: number; cardGap: number }>`
  display: flex;
  gap: 32px;
  transition: transform 0.4s ease;
  transform: translateX(calc(50% - ${p => (p.current + 6) * (p.cardWidth + p.cardGap)}px - ${p => p.cardWidth / 2}px));
  padding: 20px 0;
  justify-content: flex-start;
  align-items: center;
`;

const DesktopCard = styled.div`
  flex-shrink: 0;
  width: 320px;
  height: 420px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
  border: 1px solid ${colors.borderLight};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  }
`;

// 모바일용 스택 컨테이너  
const MobileStack = styled.div`
  position: relative;
  height: 620px;
  margin: 0 auto 32px;
  max-width: 100%;
  padding: 0 20px;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 480px) {
    height: 580px;
    padding: 0 16px;
  }
`;

const MobileCardsStage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MobileCard = styled.div<{ translateX: number; translateY: number; scale: number; z: number; opacity: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 320px;
  height: ${p => p.translateY > 0 ? 'calc(520px - 24px)' : '520px'};
  transform: translate(-50%, -50%) translate(${p => p.translateX}px, ${p => p.translateY}px) scale(${p => p.scale});
  z-index: ${p => p.z};
  opacity: ${p => p.opacity};
  background: white;
  border-radius: 24px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
  border: 1px solid ${colors.borderLight};
  transition: all 0.4s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 280px;
    height: ${p => p.translateY > 0 ? 'calc(480px - 24px)' : '480px'};
  }
`;

// 폰 목업 스타일
const PhoneMockup = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 24px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
  border: 1px solid ${colors.borderLight};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StatusBar = styled.div`
  height: 44px;
  background: #000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  color: white;
  font-size: 14px;
  font-weight: 600;
`;

const StatusTime = styled.span``;
const StatusBattery = styled.span``;

const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BatteryIcon = styled.div`
  width: 24px;
  height: 12px;
  border: 1px solid white;
  border-radius: 2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: -3px;
    top: 3px;
    width: 2px;
    height: 6px;
    background: white;
    border-radius: 0 1px 1px 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 2px;
    width: 16px;
    height: 8px;
    background: white;
    border-radius: 1px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
`;

const Tab = styled.div<{ active: boolean }>`
  flex: 1;
  padding: 12px 0;
  border-bottom: 2px solid ${p => p.active ? colors.primary : 'transparent'};
  cursor: pointer;
`;

const TabText = styled.span<{ active: boolean }>`
  font-size: 14px;
  font-weight: ${p => p.active ? '600' : '400'};
  color: ${p => p.active ? colors.primary : colors.textSecondary};
`;

const NavTab = styled.div<{ active?: boolean }>`
  flex: 1;
  padding: 12px 0;
  border-bottom: 2px solid ${p => p.active ? colors.primary : 'transparent'};
  font-size: 14px;
  font-weight: ${p => p.active ? '600' : '400'};
  color: ${p => p.active ? colors.primary : colors.textSecondary};
  text-align: center;
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: 20px;
  gap: 40px;
  overflow-y: auto;
`;

const SectionTitleMobile = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 16px;
  text-align: left;
`;

const MentorQuoteCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const MentorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const MentorAvatar = styled.div`
  font-size: 24px;
`;

const MentorName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.textPrimary};
  text-align: left;
`;

const QuoteText = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  line-height: 1.5;
  font-style: italic;
  text-align: left;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const SummaryCardTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 16px;
  text-align: left;
`;

const SummaryItem = styled.div`
  font-size: 14px;
  color: ${colors.textSecondary};
  line-height: 1.5;
  margin-bottom: 12px;
  text-align: left;
`;

const Divider = styled.div`
  height: 1px;
  background: ${colors.borderLight};
  margin: 12px 0;
`;

const GestureBar = styled.div`
  height: 34px;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid ${colors.borderLight};
`;

const GestureIndicator = styled.div`
  width: 134px;
  height: 5px;
  background: #d1d5db;
  border-radius: 3px;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: white;
  border: 1px solid ${colors.borderPrimary};
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textPrimary};
  transition: background 0.2s ease;
  z-index: 10;
  cursor: pointer;
  
  &:hover { 
    background: ${colors.backgroundSecondary}; 
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const PrevButton = styled(NavButton)`
  left: -20px;
`;

const NextButton = styled(NavButton)`
  right: -20px;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.button<{ active: boolean }>`
  width: ${p => (p.active ? 28 : 10)}px;
  height: 10px;
  border-radius: 999px;
  background: ${p => (p.active ? colors.primary : colors.borderPrimary)};
  transition: all 200ms ease;
  cursor: pointer;
  border: none;
`;

const MentorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const MentorEmoji = styled.span`
  font-size: 20px;
`;

const MentorQuote = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.textSecondary};
  margin: 0;
  text-align: left;
`;

const SummaryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0 0 12px 0;
  text-align: left;
`;

const SummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin-bottom: 4px;
`;

const SummaryItemContent = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${colors.textSecondary};
  text-align: left;
`;

const NavigationBar = styled.div`
  padding: 16px 20px 12px;
  background: white;
  border-bottom: 1px solid ${colors.borderLight};
  display: flex;
  gap: 0;
`;

const mentors = [
  { id: 'buffett', name: 'AI 멘토 워렌 버핏', icon: '', quote: '"가치투자의 핵심은 좋은 기업을 좋은 가격에 사는 것이다. 시장의 단기적 변동성은 무시하고 기업의 본질적 가치에 집중한다."', shortDesc: '가치투자의 아버지, 장기적 관점의 투자 철학' },
  { id: 'graham', name: 'AI 멘토 벤저민 그레이엄', icon: '', quote: '"안전마진을 충분히 확보하는 것이 투자의 기본 원칙이다. 내재가치 대비 저평가된 기업을 찾아 장기 보유한다."', shortDesc: '안전마진의 창시자, 위험 관리 중심 투자' },
  { id: 'wood', name: 'AI 멘토 캐시 우드', icon: '', quote: '"혁신 기술의 성장 잠재력을 과소평가하지 않는다. AI, 바이오, 신재생에너지 등 미래 기술에 대한 투자는 장기적 관점이 필요하다."', shortDesc: '혁신 기술 전문가, 미래 성장 주도' },
  { id: 'munger', name: 'AI 멘토 찰리 멍거', icon: '', quote: '"투자는 다학제적 사고를 요구한다. 심리학, 철학, 역사 등 다양한 분야의 지식을 종합하여 투자 기회를 발견한다."', shortDesc: '다학제적 사고의 철학자, 통찰력 중심' },
  { id: 'dalio', name: 'AI 멘토 레이 달리오', icon: '', quote: '"세계 경제는 하나의 거대한 시스템이다. 각국의 정책, 통화, 무역 관계를 종합적으로 분석하여 포트폴리오를 분산한다."', shortDesc: '글로벌 매크로 전문가, 시스템 사고' },
  { id: 'lynch', name: 'AI 멘토 피터 린치', icon: '', quote: '"투자 기회는 일상에서 발견된다. 소비자로서의 경험과 관찰이 투자자로서의 통찰력을 만들어낸다."', shortDesc: '일상 관찰의 대가, 소비자 중심 투자' }
];

const summaryData: {
  [key: string]: { title: string; content: string }[];
} = {
  buffett: [
    { title: '현재 삼성전자 평가', content: '현재 삼성전자는 단기 실적 부진에 따른 외국인 매도세와 심리적 불안이 뚜렷하지만, 강력한 현금흐름과 시장 지배력을 고려할 때 장기 보유 가치가 높다.' },
    { title: '투자자님의 과거 행동 분석', content: '투자자님의 과거 행동을 보면 단기 변동성에 민감하게 반응하여 조기 청산하는 패턴이 반복되었다. 이번에는 기업의 본질적 가치에 집중하여 장기 관점을 유지하시길 권한다.' },
    { title: '시장 상황 판단', content: '시장이 두려워할 때가 오히려 기회이다. 현재 삼성전자의 저평가 상황은 장기 투자자에게 매력적인 진입 시점이 될 수 있다.' }
  ],
  graham: [
    { title: '삼성전자 PER/PBR 평가', content: '삼성전자의 PER 12배, PBR 1.2배는 반도체 업종 평균 대비 저평가되어 있으며, 충분한 안전마진을 제공한다.' },
    { title: '투자자님의 이전 투자 기록', content: '투자자님의 이전 투자 기록을 보면 안전마진이 부족한 상황에서 진입하여 손실을 경험한 사례가 있다. 이번에는 내재가치 대비 30% 이상 할인된 가격에서 분할 매수하시길 권한다.' },
    { title: '기술적 지표 분석', content: '기술적 지표상 과매도 구간에서 반등 신호가 감지되며, 이는 안전마진 확보와 함께 매수 타이밍으로 적합하다.' }
  ],
  wood: [
    { title: '삼성전자 중장기 성장성', content: 'AI 시장 성장과 반도체 기술 혁신을 고려할 때, 삼성전자의 중장기 성장 잠재력은 매우 높다. 특히 메모리 반도체 분야의 기술적 우위가 돋보인다.' },
    { title: '투자자님의 포트폴리오 분석', content: '투자자님의 포트폴리오를 보면 혁신 기술 분야에 과감하게 투자하는 성향이 있다. 이번에는 단기 변동성보다 장기 성장 트렌드에 집중하여 포지션을 구축하시길 권한다.' },
    { title: '장기 성장 트렌드', content: '장기 성장 트렌드를 반영한 기술적 지표가 긍정적이며, AI 시장 확장에 따른 수요 증가가 예상된다.' }
  ],
  munger: [
    { title: '삼성전자 경제적 해자', content: '삼성전자의 경제적 해자가 견고하며, 경영진의 주주친화적 정책이 긍정적이다. 글로벌 기술 트렌드 변화에도 잘 대응하고 있다.' },
    { title: '투자자님의 투자 철학 분석', content: '투자자님의 투자 철학을 분석한 결과, 다학제적 사고로 접근하는 경향이 있다. 이번에는 기업의 문화와 경영진의 철학을 중시하여 장기적 관점에서 평가하시길 권한다.' },
    { title: '기업 가치 추이', content: '기업 가치와 주가의 괴리가 점진적으로 축소되는 추세이며, 이는 기업의 본질적 가치가 시장에서 인정받고 있음을 의미한다.' }
  ],
  dalio: [
    { title: '글로벌 반도체 사이클 및 환율', content: '글로벌 반도체 사이클과 환율 변동을 고려할 때, 현재는 매수 타이밍으로 보인다. 포트폴리오 분산 차원에서도 적절하다.' },
    { title: '투자자님의 과거 투자 패턴', content: '투자자님의 과거 투자 패턴을 보면 단일 자산에 집중하는 경향이 있다. 이번에는 반도체 섹터 내에서도 분산 투자를 고려하시길 권한다.' },
    { title: '거시경제 지표 및 기술적 신호', content: '거시경제 지표와 연관된 기술적 신호가 긍정적이며, 글로벌 경제 회복에 따른 수요 증가가 예상된다.' }
  ],
  lynch: [
    { title: '우리 일상 속 삼성 기술', content: '우리 일상 속 모든 전자제품에 삼성의 기술이 들어있다. 이런 친숙한 기업에 투자하는 것은 피터 린치의 철학과 부합한다.' },
    { title: '투자자님의 소비 패턴', content: '투자자님의 소비 패턴을 보면 삼성 제품을 자주 사용하고 있다. 이번에는 소비자로서의 경험을 바탕으로 투자 결정을 내리시길 권한다.' },
    { title: '소비자 선호도 및 브랜드 가치', content: '소비자 선호도와 브랜드 가치를 반영한 지표가 안정적이며, 일상에서 발견할 수 있는 투자 기회의 전형적 사례이다.' }
  ]
};

export const DemoSectionMobile: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [apiData, setApiData] = useState<DailyMentorAnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // API 호출 함수
  const fetchDailyMentorAnalysis = async () => {
    try {
      setIsLoading(true);

      const response = await fetch('https://getdailymentoranalysiscall-hdzqmojorq-du.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            date: new Date().toISOString().split('T')[0] // YYYY-MM-DD 형식
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('API Response:', result); // 디버깅용

      if (result.data && result.data.response) {
        setApiData(result.data.response);
      } else if (result.response) {
        // 직접 response가 있는 경우
        setApiData(result.response);
      } else if (result.result && result.result.response) {
        // result.result.response가 있는 경우 (API 응답 구조에 맞춤)
        setApiData(result.result.response);
      } else if (result.result) {
        // result.result가 있는 경우
        setApiData(result.result);
      } else {
        console.error('Invalid response format:', result);
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Failed to fetch daily mentor analysis:', err);
      // 에러가 발생해도 조용히 기본 데이터를 사용
    } finally {
      setIsLoading(false);
    }
  };

  // 컴포넌트 마운트 시 API 호출
  useEffect(() => {
    fetchDailyMentorAnalysis();
  }, []);

  // API 데이터를 기반으로 멘토 데이터 생성
  const dynamicMentors = useMemo(() => {
    if (!apiData || !apiData.analysisPerMentor) return mentors; // API 데이터가 없거나 analysisPerMentor가 없으면 기본 데이터 사용

    return apiData.analysisPerMentor.map((mentor) => ({
      id: mentor.mentorId,
      name: `AI 멘토 ${mentor.mentorNameKr}`,
      icon: '',
      quote: `"${mentor.analysisResult.ko.investmentPhilosophy}"`,
      shortDesc: `${mentor.mentorNameKr}의 투자 철학`
    }));
  }, [apiData]);

  // API 데이터를 기반으로 요약 데이터 생성
  const dynamicSummaryData = useMemo(() => {
    if (!apiData || !apiData.analysisPerMentor) return summaryData; // API 데이터가 없거나 analysisPerMentor가 없으면 기본 데이터 사용

    const newSummaryData: Record<string, SummaryItem[]> = {};
    apiData.analysisPerMentor.forEach((mentor) => {
      newSummaryData[mentor.mentorId] = [
        {
          title: '종합 평가',
          content: mentor.analysisResult.ko.overallEvaluation
        },
        {
          title: '학습 포인트',
          content: mentor.analysisResult.ko.learningPoints
        },
        {
          title: '다음 점검 시점',
          content: mentor.analysisResult.ko.nextCheckpoint
        }
      ];
    });
    return newSummaryData;
  }, [apiData]);

  const cardWidth = 320;
  const cardGap = 32;

  const handlePrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prev => (prev - 1 + dynamicMentors.length) % dynamicMentors.length);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating]);

  const handleNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prev => (prev + 1) % dynamicMentors.length);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating]);

  const handleDotClick = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating]);

  // 키보드 네비게이션
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext]);

  // 터치/스와이프 지원
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev();
    }
  };

  return (
    <DemoContainer data-section="demo">
      <DemoContent>
        <SectionTitle>
          <span className="desktop-text">실제 분석 화면을 확인해보세요</span>
          <span className="mobile-text">
            실제 분석 화면을<br />
            확인해보세요
          </span>
        </SectionTitle>
        <SectionSubtitle>
          {apiData && apiData.analysisPerMentor ?
            `${apiData.stockNameKr} 투자 고민을 예시로 ${apiData.analysisPerMentor.length}명의 투자 멘토가
                          어떻게 다른 관점에서 분석하는지 경험해보세요.` :
            `삼성전자 투자 고민을 예시로 9명의 투자 멘토가
                          어떻게 다른 관점에서 분석하는지 경험해보세요.`
          }
        </SectionSubtitle>

        {/* 로딩 상태 */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p>실시간 분석 데이터를 불러오는 중...</p>
          </div>
        )}


        {/* 메인 콘텐츠 - 로딩 중일 때만 숨김 */}
        {!isLoading && (
          <>
            {/* 데스크탑용 가로 스크롤 */}
            <DesktopCarousel>
              <DesktopCardsContainer
                ref={containerRef}
                current={currentIndex}
                cardWidth={cardWidth}
                cardGap={cardGap}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {dynamicMentors.map((mentor, index) => (
                  <DesktopCard key={mentor.id}>
                    <PhoneMockup>
                      <PhoneScreen>
                        <StatusBar>
                          <StatusTime>9:41</StatusTime>
                          <StatusIcons>
                            <BatteryIcon />
                          </StatusIcons>
                        </StatusBar>

                        <NavigationBar>
                          <TabContainer>
                            <Tab active={true}>
                              <TabText active={true}>AI 리포트</TabText>
                            </Tab>
                            <Tab active={false}>
                              <TabText active={false}>내 매매기록</TabText>
                            </Tab>
                          </TabContainer>
                        </NavigationBar>

                        <ContentContainer>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <SectionTitleMobile>고민 요약</SectionTitleMobile>

                            <MentorQuoteCard>
                              <MentorHeader>
                                <MentorName>{mentor.name}</MentorName>
                              </MentorHeader>
                              <QuoteText>{mentor.quote}</QuoteText>
                            </MentorQuoteCard>

                            <SummaryCard>
                              <SummaryCardTitle>종합 판단 요약</SummaryCardTitle>
                              {dynamicSummaryData[mentor.id]?.map((item: SummaryItem, idx: number) => (
                                <React.Fragment key={idx}>
                                  <SummaryItem>✔️ {item.title}: {item.content}</SummaryItem>
                                  {idx < (dynamicSummaryData[mentor.id]?.length || 0) - 1 && <Divider />}
                                </React.Fragment>
                              ))}
                            </SummaryCard>
                          </div>
                        </ContentContainer>

                        <GestureBar>
                          <GestureIndicator />
                        </GestureBar>
                      </PhoneScreen>
                    </PhoneMockup>
                  </DesktopCard>
                ))}
              </DesktopCardsContainer>
              <PrevButton onClick={handlePrev}>‹</PrevButton>
              <NextButton onClick={handleNext}>›</NextButton>
            </DesktopCarousel>

            {/* 모바일용 카드 스택 */}
            <MobileStack
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <MobileCardsStage>
                {dynamicMentors.map((mentor, index) => {
                  const rel = (index - currentIndex + dynamicMentors.length) % dynamicMentors.length;
                  let translateX = 0;
                  let translateY = 0;
                  let scale = 1;
                  let z = 1;
                  let opacity = 0;

                  if (rel === 0) {
                    // 현재 카드 (중앙)
                    translateX = 0;
                    translateY = 0;
                    scale = 1;
                    z = 3;
                    opacity = 1;
                  } else if (rel === 1) {
                    // 다음 카드 (오른쪽)
                    translateX = 90;
                    translateY = 10;
                    scale = 0.9;
                    z = 2;
                    opacity = 0.6;
                  } else if (rel === dynamicMentors.length - 1) {
                    // 이전 카드 (왼쪽)
                    translateX = -90;
                    translateY = 10;
                    scale = 0.9;
                    z = 2;
                    opacity = 0.6;
                  } else {
                    // 보이지 않는 카드들
                    translateX = 0;
                    translateY = 0;
                    scale = 1;
                    z = 1;
                    opacity = 0;
                  }

                  return (
                    <MobileCard
                      key={mentor.id}
                      translateX={translateX}
                      translateY={translateY}
                      scale={scale}
                      z={z}
                      opacity={opacity}
                      onClick={() => handleDotClick(index)}
                    >
                      <PhoneMockup>
                        <PhoneScreen>
                          <StatusBar>
                            <StatusTime>9:41</StatusTime>
                            <StatusIcons>
                              <BatteryIcon />
                            </StatusIcons>
                          </StatusBar>
                          <NavigationBar>
                            <NavTab active>AI 리포트</NavTab>
                            <NavTab>내 매매기록</NavTab>
                          </NavigationBar>
                          <ContentContainer>
                            <SectionTitleMobile>고민 요약</SectionTitleMobile>
                            <MentorQuoteCard>
                              <MentorInfo>
                                <MentorName>{mentor.name}</MentorName>
                              </MentorInfo>
                              <MentorQuote>{mentor.quote}</MentorQuote>
                            </MentorQuoteCard>
                            <SummaryCard>
                              <SummaryTitle>종합 판단 요약</SummaryTitle>
                              <SummaryContent>
                                {dynamicSummaryData[mentor.id]?.map((item: SummaryItem, idx: number) => (
                                  <SummaryItem key={idx}>
                                    <SummaryItemTitle>{item.title}</SummaryItemTitle>
                                    <SummaryItemContent>{item.content}</SummaryItemContent>
                                  </SummaryItem>
                                ))}
                              </SummaryContent>
                            </SummaryCard>
                          </ContentContainer>
                        </PhoneScreen>
                      </PhoneMockup>
                    </MobileCard>
                  );
                })}
              </MobileCardsStage>
            </MobileStack>

            <Dots>
              {dynamicMentors.map((_, index) => (
                <Dot
                  key={index}
                  active={index === currentIndex}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </Dots>
          </>
        )}
      </DemoContent>
    </DemoContainer>
  );
}; 